import { CircularProgress } from "@mui/material";
import { Outlet } from "react-router-dom";
import { sendCampaignTestEmail } from "@/api/campaignBuilder";
import CampaignBuilderBlocked from "@/components/campaignBuilder/CampaignBuilderBlocked";
import CampaignBuilderModal from "@/components/campaignBuilder/CampaignBuilderModal";
import CampaignBuilderNav from "@/components/campaignBuilder/CampaignBuilderNav";
import CampaignNameModal from "@/components/campaignBuilder/CampaignNameModal";
import DraftTestPanel from "@/components/campaignBuilder/DraftTestPanel";
import SendTestModal from "@/components/campaignBuilder/SendTestModal";
import ToastChip from "@/components/campaignBuilder/ToastChip";
import ValidationToast from "@/components/campaignBuilder/ValidationToast";
import PlanModal from "@/components/plan/PlanModal";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useMyEntitlements } from "@/hooks/useMyEntitlements";

export default function CampaignBuilder() {
  // The steps below render eleven entitlement gates. Every one of them has to choose a value
  // before the matrix arrives, and the safe choice is "locked" — so without this boundary a Pro
  // client's first paint shows Edit Gifts, confetti, reminders, the country filter and
  // pay-as-you-give all locked, then unlocks them a moment later. A gate that renders before it
  // knows the plan does not merely withhold: it offers to sell a paying client something they
  // already own, and a click landing in that window opens the upgrade modal.
  //
  // Held once here rather than eleven times below. The gates keep their own `ent ?` guards —
  // they must not depend on a boundary existing somewhere up the tree, and PlanModal renders as a
  // SIBLING of the Outlet rather than a child, so it is outside this anyway.
  //
  // This is the convention App.tsx already uses for permissions (`if (!isLoaded) return null`),
  // applied to entitlements.
  const { isLoaded: entitlementsLoaded, isLoading: entitlementsLoading } = useMyEntitlements();

  const {
    campaignId,
    sendTestOpen,
    closeSendTest,
    toastMessage,
    showToast,
    clearToast,
    validationIssues,
    clearValidation,
    saveStepIfDirty,
    campaignName,
    campaignNameModal,
    commitCampaignName,
    closeCampaignRename,
    canChooseWallet,
    fundingWallets,
    fundingWalletsLoading,
    fundingWalletsError,
    reloadFundingWallets,
    fundingGroupId,
    chooseFundingWallet,
    editDenied,
  } = useCampaignBuilder();

  async function handleNameSubmit(name: string, groupId: number | null) {
    if (groupId !== null && !(await chooseFundingWallet(groupId))) return false;
    return commitCampaignName(name);
  }

  async function handleSendTest(emails: string[]) {
    if (!campaignId) {
      throw new Error("Save the campaign before sending a test.");
    }
    // Step 3 only autosaves on a ~60s interval (see DesignStep.tsx) — sending a test right after
    // typing a change would otherwise read stale content straight from the draft row. Flush it
    // first so the email matches what's actually on screen; a no-op when nothing's dirty.
    const saved = await saveStepIfDirty(3);
    if (!saved) {
      throw new Error("Couldn't save your latest changes — please try again.");
    }
    const result = await sendCampaignTestEmail(campaignId, emails);
    if (result.failed > 0) {
      throw new Error(
        result.sent > 0
          ? `Sent to ${result.sent} of ${result.total} — check the failed address(es) and try again.`
          : "Couldn't send the test email. Please try again.",
      );
    }
    showToast(result.sent === 1 ? "Test email sent" : "Test emails sent");
  }

  if (editDenied) return <CampaignBuilderBlocked reason="campaign" />;

  return (
    // `h-screen` + `overflow-hidden`, not `min-h-screen`: the shell is exactly the viewport and
    // `main` is the ONLY thing that scrolls.
    //
    // With `min-h-screen` the shell grew with its content while `main` also carried
    // `overflow-y-auto`. An overflow value other than `visible` sets a flex item's automatic minimum
    // size to zero, so `main` was capped at the viewport and scrolled internally on some steps, and
    // grew past it and let the document scroll on others. Which element owned the wheel therefore
    // changed with the length of the page, which is why scrolling worked in some places and not
    // others.
    //
    // Pinning the shell to the viewport also makes `position: sticky` meaningful inside `main`:
    // sticky resolves against the nearest scrolling ancestor, so a column that never scrolls is a
    // column its children can never stick within.
    <div className="h-screen flex flex-col overflow-hidden bg-background">
      <CampaignBuilderNav />
      <main className="flex-1 overflow-y-auto">
        {/* An ERROR falls through to the steps rather than blocking. The builder stays usable with
            every paid control locked, which is the right way round: a failed request must never
            unlock anything, and a client who cannot load their plan can still write a campaign. */}
        {entitlementsLoading && !entitlementsLoaded ? (
          <div className="flex h-full items-center justify-center" aria-busy="true">
            <CircularProgress aria-label="Loading your plan" size={28} />
          </div>
        ) : (
          <Outlet />
        )}
      </main>
      <CampaignBuilderModal />
      {/* Waits for the plan: whether the create modal also asks for a funding wallet depends on it. */}
      {campaignNameModal && !entitlementsLoading && (
        <CampaignNameModal
          key={campaignNameModal}
          mode={campaignNameModal}
          initialName={campaignName}
          onSubmit={handleNameSubmit}
          onCancel={closeCampaignRename}
          wallets={canChooseWallet ? fundingWallets : undefined}
          walletsLoading={fundingWalletsLoading}
          walletsError={fundingWalletsError}
          onRetryWallets={reloadFundingWallets}
          initialGroupId={fundingGroupId}
        />
      )}
      <PlanModal />
      <SendTestModal open={sendTestOpen} onClose={closeSendTest} onSend={handleSendTest} />
      <ToastChip message={toastMessage} open={toastMessage.length > 0} onClose={clearToast} />
      <ValidationToast
        open={validationIssues.length > 0}
        issues={validationIssues}
        onClose={clearValidation}
      />
      {/* TEMP: manual draft-save/reload test panel — shows on every builder step (1–4) */}
      <DraftTestPanel />
    </div>
  );
}
